import axios from 'axios';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { Order, RelatedDocument } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import { useSelectedDomain } from './useSelectedDomain';

const documentsByOrder: Ref<Record<number, RelatedDocument[]>> = ref({});
const documentsByShipment: Ref<Record<number, RelatedDocument[]>> = ref({});

type Payload = Loadable<RelatedDocument[]>;

export interface UseRelatedDocumentsResponse {
  loadRelatedDocuments: () => Promise<Ref<Payload>>;
  numRelatedDocuments: ComputedRef<number | undefined>;
  relatedDocuments: ComputedRef<Payload>;
}

export const useRelatedDocuments = (): UseRelatedDocumentsResponse => {
  const { t } = useI18n();
  const watchSelectedDomain = useSelectedDomain();
  const toast = useToast();
  const { parentEntity } = useParentEntityStore();

  const isLoading = ref(false);
  const error: Ref<unknown> = ref();

  const isOrder = computed(() => parentEntity.value instanceof Order);
  const docsCache = computed(() => {
    return isOrder.value ? documentsByOrder.value : documentsByShipment.value;
  });

  const relatedDocuments = computed(() => {
    const entity = parentEntity.value;
    return {
      data: entity?.id ? docsCache.value[entity.id] : undefined,
      error: error.value,
      loading: isLoading.value,
    };
  });

  const numRelatedDocuments = computed(() => {
    return relatedDocuments.value.data?.length;
  });

  const loadRelatedDocuments = async (): Promise<Ref<Payload>> => {
    const entity = parentEntity.value;
    if (entity?.id == null) {
      throw new Error('Missing id for Order/Shipment');
    }

    const getDocuments = computed(() =>
      isOrder.value
        ? api.orderDocument.getRelatedDocumentsByOrderId.bind(api.orderDocument)
        : api.shipmentDocument.getRelatedDocumentsByShipmentId.bind(
            api.shipmentDocument,
          ),
    );

    isLoading.value = true;
    try {
      const docs = await getDocuments.value(entity.id);
      docsCache.value[entity.id] = docs;
      watchSelectedDomain(loadRelatedDocuments);
    } catch (e) {
      if (axios.isCancel(e)) {
        return relatedDocuments;
      }

      console.error('Error loading documents', e);
      error.value = e;
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('common.relatedParam', {
            param: t('documents.documents', 2),
          }),
        }),
        error: e,
      });
    } finally {
      isLoading.value = false;
    }

    return relatedDocuments;
  };

  return {
    loadRelatedDocuments,
    numRelatedDocuments,
    relatedDocuments,
  };
};
